import { Type, type Static } from "@sinclair/typebox";
export const CapacitySettingsSaveSchema = Type.Object(
  {
    operationId: Type.String({ format: "uuid" }),
    version: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    employeeIds: Type.Array(Type.String({ format: "uuid" }), {
      uniqueItems: true,
      maxItems: 200,
    }),
    efficiency: Type.Union([
      Type.Number({ minimum: 0.000001, maximum: 1 }),
      Type.Null(),
    ]),
  },
  { additionalProperties: false },
);
export type CapacitySettingsSave = Static<typeof CapacitySettingsSaveSchema>;
export interface CapacitySettings {
  version: number | null;
  employeeIds: string[];
  efficiency: number | null;
}
export interface CapacityWorker {
  id: string;
  name: string;
  active: boolean;
  weeklyHours: number | null;
}
export interface CapacityJobSource {
  id: string;
  code: string;
  title: string;
  parentJobId: string | null;
  accountingMode: string;
  jobKind: string;
  status: string;
  finished: boolean;
  mergedIntoJobId: string | null;
  plannedHours: number | null;
  actualHours: number;
}
export interface CapacityJob extends CapacityJobSource {
  counted: boolean;
  remainingHours: number | null;
  varianceHours: number | null;
  variancePercent: number | null;
  overrunHours: number;
}
export interface CapacityPeriod {
  from: string;
  toExclusive: string;
  availableHours: number;
  productiveHours: number;
  missingDays: number;
  saturation: number | null;
}
export interface CapacitySummary {
  workerCount: number;
  theoreticalHours: number | null;
  effectiveHours: number | null;
  knownBacklogHours: number;
  backlogHours: number | null;
  coverageWeeks: number | null;
  missingEstimates: number;
  missingSubjobEstimates: number;
  unresolvedJobs: number;
  overrunHours: number;
  jobs: CapacityJob[];
  estimationAccuracy: number | null;
  completedEstimatedJobs: number;
}
export interface ProductionCapacity {
  asOf: string;
  settings: CapacitySettings;
  workers: CapacityWorker[];
  summary: CapacitySummary;
  last4: CapacityPeriod;
  previous4: CapacityPeriod;
  last8: CapacityPeriod;
  trendPoints: number | null;
  growth: {
    key: "backlog" | "saturation" | "margin" | "cash" | "organization";
    ready: boolean;
  }[];
}
